import React, { ReactElement, useContext, useMemo, useCallback } from "react";
import { Box, CircularProgress } from "@mui/material";
import { Badge } from "@chakra-ui/react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, ICellRendererParams, ModuleRegistry, AllCommunityModule, themeAlpine } from "ag-grid-community";
import { Tooltip, IconButton } from "@mui/material";
import { MdOutlineContentCopy } from "react-icons/md";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { ProfileContext } from "../../../../store/ProfileProvider";
import AgGridPagination from "../../shared/AgGridPagination";

dayjs.extend(utc);
dayjs.extend(timezone);

ModuleRegistry.registerModules([AllCommunityModule]);

type Debit = {
  data: string;
  description: string;
};

type Credit = {
  data: string;
  description: string;
};

type RowData = {
  id: string;
  created_at: string;
  record_owner: string;
  type: string;
  debit: Debit;
  credit: Credit;
  asset: string;
  status: string;
};

const TransactionList: React.FC<{
  isLoading: boolean;
  tableData: RowData[];
  page: number;
  pageSize: number;
  rowCount: number;
  setPaginationModel: any;
}> = ({
  isLoading,
  tableData,
  page,
  pageSize,
  rowCount,
  setPaginationModel,
}) => {
  const { profileTimeZone } = useContext(ProfileContext);
  const userRole = localStorage.getItem("userRole")?.toLowerCase();

  interface RecordStatus {
    status: string;
    value: string;
    display: ReactElement;
  }

  const statusClasses = "status";

  const statusList: RecordStatus[] = [
    {
      status: "pending",
      value: "pending",
      display: (
        <Badge className={`${statusClasses} status-pending`}>Pending</Badge>
      ),
    },
    {
      status: "complete",
      value: "complete",
      display: (
        <Badge className={`${statusClasses} status-successful`}>
          Completed
        </Badge>
      ),
    },
    {
      status: "cancelled",
      value: "cancelled",
      display: (
        <Badge className={`${statusClasses} status-cancelled`}>Cancelled</Badge>
      ),
    },
    {
      status: "failed",
      value: "failed",
      display: (
        <Badge className={`${statusClasses} status-failed`}>Failed</Badge>
      ),
    },
  ];

  const handleClickCopy = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
    } catch (err) {
      console.error("Failed to copy ID: ", err);
    }
  };

  const formatDate = (timestamp: string) => {
    return dayjs(Number(timestamp))
      .tz(profileTimeZone)
      .format("DD MMM YY - hh:mm A");
  };

  const CustomLoadingOverlay = () => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
      <CircularProgress size={40} />
    </div>
  );

  // AG Grid cell renderers
  const CopyIdRenderer = (params: ICellRendererParams) => (
    <Tooltip title="Copy ID" arrow placement="top">
      <div>
        <IconButton
          id={"button-copy-id-" + params.data?.id}
          onClick={() => handleClickCopy(params.data.id)}
          size="small"
        >
          <MdOutlineContentCopy className="h-5 w-5 text-navy-700" />
        </IconButton>
      </div>
    </Tooltip>
  );

  const CreatedDateRenderer = (params: ICellRendererParams) => (
    <>{formatDate(params.data.created_at)}</>
  );

  const RecordOwnerRenderer = (params: ICellRendererParams) => (
    <>{params.data.record_owner}</>
  );

  const TypeRenderer = (params: ICellRendererParams) => (
    <>
      {userRole === "agent"
        ? String(params.data.type).replace("Agent", "")
        : params.data.type}
    </>
  );

  const DebitRenderer = (params: ICellRendererParams) => (
    <div>
      <div>{params.data.debit?.data ? params.data.debit.data : ""}</div>
      <div style={{ fontSize: "0.85rem", color: "gray" }}>
        {params.data.debit?.description ? params.data.debit.description : ""}
      </div>
    </div>
  );

  const CreditRenderer = (params: ICellRendererParams) => (
    <div>
      <div>{params.data.credit?.data ? params.data.credit.data : ""}</div>
      <div style={{ fontSize: "0.85rem", color: "gray" }}>
        {params.data.credit?.description ? params.data.credit.description : ""}
      </div>
    </div>
  );

  const StatusRenderer = (params: ICellRendererParams) => {
    const status = params.data.status;
    const statusItem = statusList.find((item) => item.status === status);
    return <>{statusItem ? statusItem.display : null}</>;
  };

  const columns: ColDef[] = [
    {
      field: "id",
      headerName: "",
      width: 50,
      sortable: false,
      resizable: false,
      cellRenderer: CopyIdRenderer,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
    },
    {
      field: "created_at",
      headerName: "Created Date",
      sortable: false,
      minWidth: 140,
      flex: 1,
      cellRenderer: CreatedDateRenderer,
    },
    {
      field: "record_owner",
      headerName: "",
      sortable: false,
      minWidth: 100,
      flex: 1,
      cellRenderer: RecordOwnerRenderer,
    },
    {
      field: "type",
      headerName: "Type",
      sortable: false,
      minWidth: 100,
      flex: 1,
      cellRenderer: TypeRenderer,
    },
    {
      field: "debit",
      headerName: "Debit",
      sortable: false,
      minWidth: 160,
      flex: 1.5,
      cellRenderer: DebitRenderer,
      autoHeight: true,
    },
    {
      field: "credit",
      headerName: "Credit",
      sortable: false,
      minWidth: 160,
      flex: 1.5,
      cellRenderer: CreditRenderer,
      autoHeight: true,
    },
    {
      field: "asset",
      headerName: "Asset",
      sortable: false,
      width: 70,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
    },
    {
      field: "status",
      headerName: "",
      sortable: false,
      width: 150,
      cellRenderer: StatusRenderer,
      cellClass: "ag-cell-status",
    },
  ];

  const filterColumns = useCallback(() => {
    let rebuildColumns = [...columns];
    if (userRole !== "superadmin" && userRole !== "agent" && userRole !== "merchant") {
      const excludeFields = ["record_owner"];
      rebuildColumns = rebuildColumns.filter(
        (column) => !excludeFields.includes(column.field!)
      );
    }
    return rebuildColumns;
  }, [userRole]);

  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      suppressMenu: true,
    }),
    []
  );

  const hidePagination = page === 0 && rowCount < pageSize;

  return (
    <>
      <Box className="card w-full">
        <div style={{ width: "100%" }}>
          <AgGridReact
            theme={themeAlpine}
            rowData={isLoading ? [] : tableData}
            columnDefs={filterColumns()}
            defaultColDef={defaultColDef}
            getRowId={(params) => params.data.id}
            rowHeight={48}
            headerHeight={44}
            domLayout="autoHeight"
          suppressDragLeaveHidesColumns={true}
          enableCellTextSelection={true}
          ensureDomOrder={true}
            suppressPaginationPanel={true}
            loading={isLoading}
            loadingOverlayComponent={CustomLoadingOverlay}
            overlayNoRowsTemplate="No data available"
          />
        </div>

        <AgGridPagination
          page={page}
          pageSize={pageSize}
          rowCount={rowCount}
          onPaginationChange={setPaginationModel}
          hidden={hidePagination}
        />
      </Box>
    </>
  );
};

export default TransactionList;
